import * as THREE from "three";
import { MeshRefractionMaterial, useGLTF } from "@react-three/drei";
import { useLoader } from "@react-three/fiber";
import { useContext, useEffect, useMemo } from "react";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import stateContext from "../../Context/StateContext";

/* ---------- GLB Selector ---------- */
const getCharmGLB = (size) => {
  switch (size) {
    case "2 mm":
      return `${process.env.PUBLIC_URL}/assets/glbs/Chain_Charms_2mm.glb`;
    case "3 mm":
      return `${process.env.PUBLIC_URL}/assets/glbs/Chain_Charms_3mm.glb`;
    case "4 mm":
      return `${process.env.PUBLIC_URL}/assets/glbs/Chain_Charms_4mm.glb`;
    default:
      return `${process.env.PUBLIC_URL}/assets/glbs/Chain_Charms_2mm.glb`;
  }
};

export const AddCharms = () => {
  const globalState = useContext(stateContext);

  /* ---------- Select GLB ---------- */
  const charmGLB = useMemo(
    () => getCharmGLB(globalState.activeCharm.size),
    [globalState.activeCharm.size]
  );

  /* ---------- Load GLTF (KEYED TO FORCE REMOUNT) ---------- */
  const { nodes, scene } = useGLTF(charmGLB);

  /* ---------- HDRIs ---------- */
  const diamondEnv = useLoader(
    RGBELoader,
    `${process.env.PUBLIC_URL}/assets/textures/hdri/ROOM-F - 1.1.hdr`
  );
  const zirconEnv = useLoader(
    RGBELoader,
    `${process.env.PUBLIC_URL}/assets/textures/hdri/studio.hdr`
  );

  /* ---------- Base Scene (CLONED ONCE) ---------- */
  const baseScene = useMemo(() => scene.clone(true), [scene]);

  /* ---------- Apply Material + Hide Diamond (SAFE) ---------- */
  useEffect(() => {
    baseScene.traverse((obj) => {
      if (obj.isMesh) {
        obj.material = globalState.globalMaterial.default.clone();
      }
      if (obj.name === "rounddiamond") {
        obj.visible = false;
      }
    });
  }, [baseScene, globalState.globalMaterial]);

  /* ---------- Extract Diamond Data (WORLD SPACE) ---------- */
  const diamondData = useMemo(() => {
    const diamond = nodes?.rounddiamond;
    if (!diamond) return null;

    diamond.updateWorldMatrix(true, false);

    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3();

    diamond.matrixWorld.decompose(position, quaternion, scale);

    return { geometry: diamond.geometry.clone(), position, quaternion, scale };
  }, [nodes]);

  /* ---------- Render ---------- */
  return (
    <group>
      {globalState.chainCharmDetails.map((detail, index) => (
        <group
          key={`${globalState.activeCharm.size}-${index}`} // 🔑 Key ensures GLTF remount on size change
          name={detail.side}
          position={detail.pos}
          scale={[1, 1, 1]}
          rotation={[0, 0, detail.side === "left" ? Math.PI / 6 : -Math.PI / 6]}
        >
          {/* Diamond */}
          {diamondData && (
            <mesh
              geometry={diamondData.geometry}
              position={diamondData.position}
              quaternion={diamondData.quaternion}
              scale={diamondData.scale}
            >
              <MeshRefractionMaterial
                envMap={detail.charm === "Diamond" ? diamondEnv : zirconEnv}
              />
            </mesh>
          )}

          {/* Chain — UNIQUE CLONE PER INSTANCE */}
          <primitive
            key={`${charmGLB}-${index}`} // Force primitive remount on GLB change
            object={baseScene.clone(true)}
          />
        </group>
      ))}
    </group>
  );
};

/* ---------- Preload GLBs ---------- */
useGLTF.preload(`${process.env.PUBLIC_URL}/assets/glbs/Chain_Charms_2mm.glb`);
useGLTF.preload(`${process.env.PUBLIC_URL}/assets/glbs/Chain_Charms_3mm.glb`);
useGLTF.preload(`${process.env.PUBLIC_URL}/assets/glbs/Chain_Charms_4mm.glb`);
